import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ScanLine, User, Package, Trash2, CheckCircle, AlertCircle, Clock, RotateCcw, Users, TrendingUp, Send, Tv } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const PiecesProcessed = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionDisplayName = SLUG_TO_DISPLAY[slug] || slug;
  const apiBase = `/per-section/${slug}/pieces-processed`;
  const backRoute = `/sections/dispatch/${slug}`;
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  const [bundleCode, setBundleCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pending bundles (not yet submitted)
  const [pendingBundles, setPendingBundles] = useState([]);
  
  // Session scans (already saved to backend)
  const [sessionScans, setSessionScans] = useState([]);
  const [staffStats, setStaffStats] = useState({ 
    today_pieces: 0, 
    today_bundles: 0,
    month_pieces: 0, 
    month_bundles: 0 
  });
  
  // Supervisor view
  const [showSupervisorView, setShowSupervisorView] = useState(false);
  const [allStaffPerformance, setAllStaffPerformance] = useState([]);
  const [performanceFilter, setPerformanceFilter] = useState('today');
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  useEffect(() => {
    staffInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (staffIdLocked && staffId) {
      loadSessionScans();
      loadStaffStats();
    }
  }, [staffIdLocked, staffId]);

  const loadSessionScans = async () => {
    try {
      const response = await apiClient.get(`${apiBase}/session/${encodeURIComponent(staffId)}`);
      setSessionScans(response.data.scans || []);
    } catch (error) {
      console.error('Error loading session scans:', error);
    }
  };

  const loadStaffStats = async () => {
    try {
      const response = await apiClient.get(`${apiBase}/staff-stats/${encodeURIComponent(staffId)}`);
      setStaffStats({
        today_pieces: response.data.today_pieces || 0,
        today_bundles: response.data.today_bundles || 0,
        month_pieces: response.data.month_pieces || 0,
        month_bundles: response.data.month_bundles || 0
      });
    } catch (error) {
      console.error('Error loading staff stats:', error);
    }
  };

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
      setPendingBundles([]); // Clear any pending bundles
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    }
  };

  const handleChangeStaff = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setStaffName('');
    setStaffStats({ today_pieces: 0, today_bundles: 0, month_pieces: 0, month_bundles: 0 });
    setSessionScans([]);
    setPendingBundles([]);
    setTimeout(() => staffInputRef.current?.focus(), 100);
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    
    if (!bundleCode.trim()) {
      toast.error('Please scan a bundle code');
      setBundleCode('');
      bundleInputRef.current?.focus();
      return;
    }

    const code = bundleCode.trim();
    
    // Check if bundle already in pending list
    if (pendingBundles.some(b => b.bundle_code === code)) {
      toast.error('Bundle already scanned');
      setBundleCode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsProcessing(true);
    try {
      // Validate bundle and get piece count without saving
      // Also validates if bundle is assigned to this staff
      const response = await apiClient.get(`${apiBase}/validate-bundle`, {
        params: { bundle_code: code, staff_id: staffId }
      });

      // Add to pending bundles list
      const bundleInfo = {
        bundle_code: code,
        usn_count: response.data.usn_count || 0,
        source_section: response.data.source_section || '',
        scanned_at: new Date().toISOString()
      };
      
      setPendingBundles(prev => [bundleInfo, ...prev]);
      toast.success(`Bundle added: ${response.data.usn_count} pieces`);
      
      setBundleCode('');
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to validate bundle';
      toast.error(errorMsg);
      setBundleCode('');
    } finally {
      setIsProcessing(false);
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const handleRemovePendingBundle = (bundleCode) => {
    setPendingBundles(prev => prev.filter(b => b.bundle_code !== bundleCode));
    toast.success('Bundle removed');
    bundleInputRef.current?.focus();
  };

  const handleSubmitBundles = async () => {
    if (pendingBundles.length === 0) {
      toast.error('No bundles to submit');
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit all pending bundles
      const response = await apiClient.post(`${apiBase}/submit-batch`, {
        staff_id: staffId,
        bundles: pendingBundles.map(b => b.bundle_code)
      });

      const successCount = response.data.success_count || 0;
      const totalPieces = response.data.total_pieces || 0;
      
      toast.success(`Submitted ${successCount} bundles (${totalPieces} pieces)`);
      
      // Clear pending bundles and reset to staff ID input
      setPendingBundles([]);
      setStaffIdLocked(false);
      setStaffId('');
      setStaffName('');
      setStaffStats({ today_pieces: 0, today_bundles: 0, month_pieces: 0, month_bundles: 0 });
      setSessionScans([]);
      
      // Focus back to staff ID input
      setTimeout(() => staffInputRef.current?.focus(), 100);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit bundles';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveScan = async (entry_id) => {
    try {
      const entry = sessionScans.find(s => s.entry_id === entry_id);
      await apiClient.delete(`${apiBase}/remove/${encodeURIComponent(entry_id)}`);
      
      setSessionScans(prev => prev.filter(s => s.entry_id !== entry_id));
      
      // Update stats
      if (entry) {
        setStaffStats(prev => ({
          ...prev,
          today_pieces: Math.max(0, prev.today_pieces - entry.usn_count),
          today_bundles: Math.max(0, prev.today_bundles - 1),
          month_pieces: Math.max(0, prev.month_pieces - entry.usn_count)
        }));
      }
      
      toast.success('Entry removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove entry');
    }
  };

  const loadAllStaffPerformance = async (filter = 'today') => {
    setLoadingPerformance(true);
    try {
      const response = await apiClient.get(`${apiBase}/all-staff-performance?date_filter=${filter}`);
      setAllStaffPerformance(response.data.staff_performance || []);
    } catch (error) {
      toast.error('Failed to load performance data');
    } finally {
      setLoadingPerformance(false);
    }
  };

  const handleShowSupervisorView = () => {
    setShowSupervisorView(true);
    loadAllStaffPerformance(performanceFilter);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-IN', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const pendingPieces = pendingBundles.reduce((sum, b) => sum + (b.usn_count || 0), 0);
  const sessionPieces = sessionScans.reduce((sum, s) => sum + (s.usn_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backRoute)}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Pieces Processed</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionDisplayName} - Staff Work Recording</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleShowSupervisorView}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
              data-testid="supervisor-view-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              All Staff Performance
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`/sections/dispatch/${slug}/pieces-processed/tv?filter=${performanceFilter}`, '_blank')}
              className="border-[#E8DCC8] text-[#E8DCC8] hover:bg-white/10"
              data-testid="tv-display-btn"
            >
              <Tv className="w-4 h-4 mr-2" />
              TV Display
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Staff ID Section */}
        <Card className="border-2 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-[#2D5F4F]" />
              Staff Identification
            </CardTitle>
            <CardDescription>
              Scan or enter staff ID to begin recording
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput
                inputRef={staffInputRef}
                onValidated={handleStaffValidated}
                autoFocus={true}
                label=""
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-[#2D5F4F] text-white px-4 py-2 rounded-lg font-mono text-lg">
                    {staffId}
                  </div>
                  {staffName && <span className="text-[#5A5F6E]">{staffName}</span>}
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleChangeStaff}
                  className="border-[#E5DFD0]"
                  data-testid="change-staff-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Change Staff
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Display */}
        {staffIdLocked && (
          <div className="grid grid-cols-2 gap-4">
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="py-4 text-center">
                <p className="text-sm text-green-600 mb-1">Today's Pieces</p>
                <p className="text-4xl font-bold text-green-700" data-testid="today-pieces">
                  {staffStats.today_pieces}
                </p>
                <p className="text-xs text-green-500">{staffStats.today_bundles} bundles</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="py-4 text-center">
                <p className="text-sm text-blue-600 mb-1">This Month</p>
                <p className="text-4xl font-bold text-blue-700" data-testid="month-pieces">
                  {staffStats.month_pieces}
                </p>
                <p className="text-xs text-blue-500">{staffStats.month_bundles} bundles</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bundle Scanning Section */}
        {staffIdLocked && (
          <Card className="border-2 border-[#2D5F4F]">
            <CardHeader className="pb-3 bg-[#2D5F4F]/5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ScanLine className="w-5 h-5 text-[#2D5F4F]" />
                Scan Bundle Barcode
              </CardTitle>
              <CardDescription>
                Scan bundles, then click Submit when done
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleBundleScan} className="flex gap-3">
                <div className="flex-1">
                  <Input
                    ref={bundleInputRef}
                    value={bundleCode}
                    onChange={(e) => setBundleCode(e.target.value)}
                    placeholder="Scan bundle barcode..."
                    className="text-lg h-14 border-2 border-[#2D5F4F] focus:border-[#2D5F4F] font-mono"
                    disabled={isProcessing || isSubmitting}
                    data-testid="bundle-input"
                    autoComplete="off"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="h-14 px-8 bg-[#2D5F4F] hover:bg-[#234A3D] text-lg"
                  disabled={isProcessing || isSubmitting}
                  data-testid="scan-bundle-btn"
                >
                  {isProcessing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Package className="w-5 h-5 mr-2" />
                      Add
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Pending Bundles List */}
        {staffIdLocked && pendingBundles.length > 0 && (
          <Card className="border-2 border-amber-400 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
                <Package className="w-5 h-5" />
                Scanned Bundles (Pending Submit)
              </CardTitle>
              <CardDescription className="text-amber-700">
                {pendingBundles.length} bundles · {pendingPieces} pieces
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {pendingBundles.map((bundle, index) => (
                  <div 
                    key={bundle.bundle_code} 
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
                    data-testid={`pending-bundle-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-amber-600 font-bold w-8">#{index + 1}</span>
                      <div>
                        <p className="font-mono font-semibold text-[#1A1F2E]">{bundle.bundle_code}</p>
                        <p className="text-xs text-[#5A5F6E]">
                          <span className="font-semibold text-green-600">+{bundle.usn_count} pcs</span>
                          {bundle.source_section && ` · ${bundle.source_section}`}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemovePendingBundle(bundle.bundle_code)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-pending-${index}`}
                      disabled={isSubmitting}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              {/* Submit Button */}
              <Button 
                onClick={handleSubmitBundles}
                className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                disabled={isSubmitting || pendingBundles.length === 0}
                data-testid="submit-bundles-btn"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                Submit {pendingBundles.length} Bundles ({pendingPieces} pieces)
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Session Count Display - Only show if no pending bundles */}
        {staffIdLocked && pendingBundles.length === 0 && (
          <Card className="border-2 border-[#E5DFD0] bg-gradient-to-r from-[#2D5F4F]/5 to-transparent">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5F6E] mb-1">Current Session</p>
                  <p className="text-5xl font-bold text-[#2D5F4F]" data-testid="session-pieces">
                    {sessionPieces}
                  </p>
                  <p className="text-sm text-[#5A5F6E] mt-1">pieces from {sessionScans.length} bundles</p>
                </div>
                <div className="text-right">
                  <CheckCircle className="w-12 h-12 text-green-500 mb-2" />
                  <p className="text-sm text-[#5A5F6E]">Auto-saved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Session Scans List */}
        {staffIdLocked && sessionScans.length > 0 && (
          <Card className="border-2 border-[#E5DFD0]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5 text-[#5A5F6E]" />
                Today's Scans
              </CardTitle>
              <CardDescription>
                {sessionScans.length} bundles · {sessionPieces} pieces
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {sessionScans.map((scan, index) => (
                  <div 
                    key={scan.entry_id} 
                    className="flex items-center justify-between p-3 bg-[#F5F1E8] rounded-lg"
                    data-testid={`scan-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-[#5A5F6E] w-8">#{sessionScans.length - index}</span>
                      <div>
                        <p className="font-mono font-semibold text-[#1A1F2E]">{scan.bundle_code}</p>
                        <p className="text-xs text-[#5A5F6E]">
                          <span className="font-semibold text-green-600">+{scan.usn_count} pcs</span>
                          {scan.source_section && ` · ${scan.source_section}`}
                          {' · '}
                          {formatTime(scan.scanned_at)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveScan(scan.entry_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-scan-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {staffIdLocked && sessionScans.length === 0 && (
          <Card className="border-2 border-dashed border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <ScanLine className="w-16 h-16 mx-auto text-[#E5DFD0] mb-4" />
              <p className="text-[#5A5F6E]">No bundles scanned today</p>
              <p className="text-sm text-[#5A5F6E]/70 mt-1">Start scanning bundle barcodes</p>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="border border-[#E5DFD0] bg-blue-50/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">How it Works:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Scan your Staff ID to start</li>
                  <li>Scan each bundle's barcode</li>
                  <li>System counts USNs in bundle and adds to your total</li>
                  <li>Daily count resets at midnight</li>
                  <li>Monthly count is used for wage calculation</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Supervisor View Dialog */}
      <Dialog open={showSupervisorView} onOpenChange={setShowSupervisorView}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              All Staff Performance
            </DialogTitle>
            <DialogDescription>
              View pieces processed by all dispatch staff
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={performanceFilter} onValueChange={(v) => { setPerformanceFilter(v); loadAllStaffPerformance(v); }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="today">Today</TabsTrigger>
              <TabsTrigger value="month">This Month</TabsTrigger>
            </TabsList>
            
            <TabsContent value={performanceFilter} className="mt-4">
              {loadingPerformance ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-[#2D5F4F] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>Staff ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Pieces</TableHead>
                      <TableHead className="text-right">Bundles</TableHead>
                      <TableHead>Last Scan</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allStaffPerformance.map((staff, index) => (
                      <TableRow key={staff.staff_id}>
                        <TableCell className="font-bold">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </TableCell>
                        <TableCell className="font-mono">{staff.staff_id}</TableCell>
                        <TableCell>{staff.staff_name}</TableCell>
                        <TableCell className="text-right font-bold text-green-600">
                          {staff.total_pieces}
                        </TableCell>
                        <TableCell className="text-right">{staff.total_bundles}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {staff.last_scan ? new Date(staff.last_scan).toLocaleTimeString() : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {allStaffPerformance.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                          No data for this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
              
              {/* Totals */}
              {allStaffPerformance.length > 0 && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg flex justify-around">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Staff</p>
                    <p className="text-2xl font-bold">{allStaffPerformance.length}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Pieces</p>
                    <p className="text-2xl font-bold text-green-600">
                      {allStaffPerformance.reduce((sum, s) => sum + s.total_pieces, 0)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Bundles</p>
                    <p className="text-2xl font-bold">
                      {allStaffPerformance.reduce((sum, s) => sum + s.total_bundles, 0)}
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PiecesProcessed;
